import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Step, StepLabel, Stepper, TextField, Typography, useMediaQuery } from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HubIcon from '@mui/icons-material/Hub';
import MicIcon from '@mui/icons-material/Mic';
import MicExternalOnIcon from '@mui/icons-material/InterpreterMode';
import StopIcon from '@mui/icons-material/Stop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useWebSocket } from './WebSocketContext';
import LoadingScreen from './LoadingScreen';
import ConnectorWizardHost from './ConnectorWizardHost';
import SettingDatasources from './SettingDatasources';
import ManualCreationDialog from './ManualCreationDialog';
import TemplateSelectionDialog from './TemplateSelectionDialog';
import AudioReviewDialog from './AudioReviewDialog';
import RecordingDialog from './RecordingDialog';
import useAudioRecorder from './useAudioRecorder';
import {
  clearCaseCreation,
  isCaseCreationActive,
  startCaseCreation,
} from '../Utils/caseCreationState';
import { apiRequest } from '../Utils/apiClient';
import logo from '../images/logowithname2.png';

const primaryColor = '#5757b0';
const primaryColorTransparent = 'rgba(87, 87, 176, 0.8)';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';
const TEMP_PROJECT_TITLE = '__temporary_project__';
const TEMP_PROJECT_DESCRIPTION = '__temporary_project_description__';
const BOOTSTRAP_TARGET_COUNT = 5;
const BOOTSTRAP_TIMEOUT_MS = 120000;
const datasourceCardSx = {
  p: { xs: 3, sm: 4 },
  borderRadius: '24px',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
  boxShadow: '0 10px 30px rgba(87, 87, 176, 0.08)',
  border: '2px solid rgba(87, 87, 176, 0.22)',
  minHeight: { xs: 'unset', lg: '100%' },
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.25s ease',
};
const datasourceCardHeaderSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.5,
  mb: 2,
  width: '100%',
};
const datasourceCardBodySx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};
const datasourceCardSubtitleSx = {
  color: 'text.secondary',
  lineHeight: 1.7,
  maxWidth: 320,
  minHeight: 48,
  mb: 2,
};
const wizardLauncherButtonSx = {
  minWidth: 0,
  width: '100%',
  maxWidth: 320,
  px: 2.75,
  py: 1.35,
  borderRadius: '999px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1.2,
  color: primaryColor,
  borderColor: 'rgba(87, 87, 176, 0.25)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
  boxShadow: '0 8px 24px rgba(87, 87, 176, 0.08)',
  '&:hover': {
    borderColor: primaryColor,
    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(241,244,255,1) 100%)',
    boxShadow: '0 12px 28px rgba(87, 87, 176, 0.12)',
  },
};

const formatBytes = (bytes = 0) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const mergeDatasourceFiles = (currentFiles = [], nextFiles = []) => {
  const merged = [...currentFiles];
  const seen = new Set(
    currentFiles.map((file) => `${file?.name || ''}::${file?.size ?? 'unknown'}`)
  );

  nextFiles.forEach((file) => {
    const key = `${file?.name || ''}::${file?.size ?? 'unknown'}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(file);
  });

  return merged;
};

const resolveBootstrapTargetCount = (queuedCount, fallbackTarget = BOOTSTRAP_TARGET_COUNT) => {
  const safeFallbackTarget = Number.isFinite(Number(fallbackTarget))
    ? Math.max(1, Number(fallbackTarget))
    : BOOTSTRAP_TARGET_COUNT;
  const safeQueuedCount = Number.isFinite(Number(queuedCount))
    ? Math.max(0, Number(queuedCount))
    : 0;

  if (safeQueuedCount > 0) {
    return Math.min(safeFallbackTarget, safeQueuedCount);
  }

  return safeFallbackTarget;
};

const AddProject = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscribeToMessages, sendMessage, serverInfo, isConnected } = useWebSocket();
  const resumeCaseId = searchParams.get('resumeCaseId');

  const [status, setStatus] = useState('datasource');
  const [goal, setGoal] = useState('');
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [serverVersion, setServerVersion] = useState(null);
  const [temporaryCaseId, setTemporaryCaseId] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const [autoOpenConnector, setAutoOpenConnector] = useState('');
  const [connectorWizardRequest, setConnectorWizardRequest] = useState(null);
  const [isPreparingTemporaryProject, setIsPreparingTemporaryProject] = useState(false);
  const [isUploadingDatasource, setIsUploadingDatasource] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedDatasourceFiles, setUploadedDatasourceFiles] = useState([]);
  const [datasourceUploadError, setDatasourceUploadError] = useState('');
  const [bootstrapError, setBootstrapError] = useState('');
  const [isMixedConsentOpen, setIsMixedConsentOpen] = useState(false);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [manualCreationDialogOpen, setManualCreationDialogOpen] = useState(false);
  const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
  const [bootstrapProgress, setBootstrapProgress] = useState({
    phase: 'waiting',
    processedCount: 0,
    queuedCount: 0,
    targetCount: BOOTSTRAP_TARGET_COUNT,
    message: '',
    latestItem: null,
    sourceType: '',
  });

  const shouldAwaitCreationUpdatesRef = useRef(false);
  const temporaryProjectCreationRef = useRef(false);
  const manualProjectCreationRef = useRef(false);
  const temporaryProjectPromiseResolveRef = useRef(null);
  const temporaryProjectPromiseRejectRef = useRef(null);
  const lastResumeCaseIdRef = useRef(resumeCaseId);

  const rotatingPlaceholders = useMemo(
    () => [
      t('addcase.input_placeholder'),
      t('addcase.input_placeholder_accountant'),
      t('addcase.input_placeholder_sales'),
      t('addcase.input_placeholder_hr'),
    ],
    [t]
  );
  const maxRecordingDurationSeconds = useMemo(() => {
    const configured = Number(serverInfo?.maxAudioRecordingLengthSeconds);
    if (Number.isFinite(configured) && configured > 0) {
      return Math.floor(configured);
    }
    return undefined;
  }, [serverInfo]);
  const {
    canUseDisplayAudio,
    clearPendingAudio: clearPendingAudioReview,
    isAudioReviewOpen,
    isRecording: isRecordingAudio,
    pendingAudio: pendingAudioReview,
    recordingElapsed,
    setIsAudioReviewOpen,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    waveformHistory,
  } = useAudioRecorder({
    isMobile,
    maxRecordingDurationSeconds,
    onError: (errorKey) => {
      if (errorKey === 'system_audio_unavailable') {
        setDatasourceUploadError(
          t(
            'addcase.system_audio_unavailable',
            'System audio capture requires screen sharing; recording microphone only.'
          )
        );
        return;
      }
      if (errorKey === 'audio_recording_empty') {
        setDatasourceUploadError(t('addcase.audio_recording_empty', 'No audio captured.'));
        return;
      }
      if (errorKey === 'audio_recording_unsupported') {
        setDatasourceUploadError(t('addcase.audio_recording_unsupported', 'Audio recording is not supported in this browser.'));
        return;
      }
      setDatasourceUploadError(t('addcase.audio_recording_error', 'Recording failed.'));
    },
  });

  useEffect(() => {
    if (!rotatingPlaceholders.length) {
      return undefined;
    }

    let timeoutId;
    let exampleIndex = 0;
    let characterIndex = 0;
    const typeSpeedMs = 38;
    const deleteSpeedMs = 20;
    const pauseAfterTypingMs = 1300;
    const pauseBeforeTypingMs = 250;

    const startTyping = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';
      if (characterIndex < fullText.length) {
        characterIndex += 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startTyping, typeSpeedMs);
        return;
      }
      timeoutId = setTimeout(startDeleting, pauseAfterTypingMs);
    };

    const startDeleting = () => {
      const fullText = rotatingPlaceholders[exampleIndex] || '';
      if (characterIndex > 0) {
        characterIndex -= 1;
        setTypedPlaceholder(fullText.slice(0, characterIndex));
        timeoutId = setTimeout(startDeleting, deleteSpeedMs);
        return;
      }
      exampleIndex = (exampleIndex + 1) % rotatingPlaceholders.length;
      timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);
    };

    setTypedPlaceholder('');
    timeoutId = setTimeout(startTyping, pauseBeforeTypingMs);
    return () => clearTimeout(timeoutId);
  }, [rotatingPlaceholders]);

  useEffect(() => {
    let mounted = true;
    fetch('/api/server_status')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (mounted && data?.server_version) {
          setServerVersion(data.server_version);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  const beginTemporaryProjectCreation = useCallback(() => {
    temporaryProjectCreationRef.current = true;
    shouldAwaitCreationUpdatesRef.current = false;
    setUpdateMessage('');
    setDatasourceUploadError('');
    setIsPreparingTemporaryProject(true);
    sendMessage({
      command: 'AddProject',
      folder: '',
      title: TEMP_PROJECT_TITLE,
      description: TEMP_PROJECT_DESCRIPTION,
      goal: '',
      customFields: [],
      attachments: [],
      defer_ai_processing: true,
    });
  }, [sendMessage]);

  const ensureTemporaryProject = useCallback(() => {
    if (temporaryCaseId) {
      return Promise.resolve(temporaryCaseId);
    }
    if (temporaryProjectCreationRef.current) {
      return new Promise((resolve, reject) => {
        temporaryProjectPromiseResolveRef.current = resolve;
        temporaryProjectPromiseRejectRef.current = reject;
      });
    }

    beginTemporaryProjectCreation();
    return new Promise((resolve, reject) => {
      temporaryProjectPromiseResolveRef.current = resolve;
      temporaryProjectPromiseRejectRef.current = reject;
    });
  }, [beginTemporaryProjectCreation, temporaryCaseId]);

  const handleDatasourceUploadResult = useCallback((data = {}) => {
    const queued = Array.isArray(data?.queued)
      ? data.queued
      : (Array.isArray(data?.files) ? data.files : []);
    const failed = Array.isArray(data?.failed) ? data.failed : [];
    setIsUploadingDatasource(false);
    setUploadProgress(100);
    if (queued.length > 0) {
      setUploadedDatasourceFiles((current) => {
        return mergeDatasourceFiles(
          current,
          queued.map((name) => ({ name, size: null }))
        );
      });
      setDatasourceUploadError('');
      setStatus('bootstrap');
      setBootstrapProgress((current) => ({
        ...current,
        phase: 'ingesting',
        queuedCount: Math.max(current.queuedCount || 0, queued.length),
        targetCount: resolveBootstrapTargetCount(
          Math.max(current.queuedCount || 0, queued.length),
          current.targetCount
        ),
        message: current.sourceType === 'connector'
          ? current.message
          : t(
            'addcase.bootstrap_ingesting_message',
            'Uploading is complete. We are ingesting the first items to prepare your project question...'
          ),
      }));
    } else if (failed.length > 0 || data?.status === 'error') {
      setDatasourceUploadError(
        data?.message || t('addcase.datasource_upload_error', 'Unable to upload the dropped files.')
      );
      setStatus('datasource');
    }
  }, [t]);

  const uploadDatasourceFiles = useCallback(async (targetCaseId, files) => {
    const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!targetCaseId || fileList.length === 0) {
      return;
    }

    setDatasourceUploadError('');
    setIsUploadingDatasource(true);
    setUploadProgress(5);

    try {
      const formData = new FormData();
      formData.append('caseid', String(targetCaseId));
      fileList.forEach((file) => {
        formData.append('files', file, file.name);
      });
      setUploadProgress(75);
      const payload = await apiRequest('/api/v1/projects/upload', {
        method: 'POST',
        body: formData,
      });
      handleDatasourceUploadResult(payload);
    } catch (error) {
      console.error('[AddProject] Failed to upload datasource files:', error);
      setIsUploadingDatasource(false);
      setUploadProgress(0);
      setDatasourceUploadError(
        error?.message || t('addcase.datasource_upload_error', 'Unable to upload the dropped files.')
      );
      setStatus('datasource');
    }
  }, [handleDatasourceUploadResult, t]);

  const beginBootstrapForDatasource = useCallback((queuedCount, sourceType, message) => {
    setStatus('bootstrap');
    setBootstrapReady(false);
    setBootstrapError('');
    setBootstrapProgress({
      phase: sourceType === 'connector' ? 'ingesting' : 'uploading',
      processedCount: 0,
      queuedCount,
      targetCount: resolveBootstrapTargetCount(queuedCount),
      message,
      latestItem: null,
      sourceType,
    });
  }, []);

  const handleDatasourceDrop = useCallback((acceptedFiles) => {
    const fileList = Array.isArray(acceptedFiles) ? acceptedFiles.filter(Boolean) : [];
    if (fileList.length === 0) {
      return;
    }

    beginBootstrapForDatasource(
      fileList.length,
      'upload',
      t(
        'addcase.bootstrap_uploading_message',
        'Uploading your datasource and preparing the first items for project setup...'
      )
    );

    setUploadedDatasourceFiles((current) => mergeDatasourceFiles(
      current,
      fileList.map((file) => ({
        name: file.name,
        size: file.size,
      }))
    ));

    ensureTemporaryProject()
      .then((createdCaseId) => {
        uploadDatasourceFiles(createdCaseId, fileList);
      })
      .catch(() => {
        setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to upload the dropped files.'));
      });
  }, [beginBootstrapForDatasource, ensureTemporaryProject, t, uploadDatasourceFiles]);

  const handleDatasourceBeforeOpen = useCallback(async () => true, []);

  const handleDatasourceConfigured = useCallback((details = {}) => {
    setAutoOpenConnector('');
    setBootstrapError('');
    const resolvedCaseId = details?.caseid || temporaryCaseId;
    if (resolvedCaseId) {
      setTemporaryCaseId(resolvedCaseId);
    }
    setStatus('bootstrap');
    setBootstrapReady(false);
    setBootstrapProgress({
      phase: 'ingesting',
      processedCount: 0,
      queuedCount: 0,
      targetCount: BOOTSTRAP_TARGET_COUNT,
      message: t(
        'addcase.bootstrap_connector_message',
        'Preparing the first connector events for project setup...'
      ),
      latestItem: null,
      sourceType: details?.type || 'connector',
    });

    const normalizedDatasourceId = Number(details?.datasourceId);
    if (resolvedCaseId && Number.isFinite(normalizedDatasourceId)) {
      sendMessage({
        command: 'forceRunDatasource',
        caseid: resolvedCaseId,
        datasource_id: normalizedDatasourceId,
      });
    }
  }, [sendMessage, t, temporaryCaseId]);

  const handleUploadRecordedAudio = useCallback(() => {
    if (!pendingAudioReview?.file) {
      return;
    }

    beginBootstrapForDatasource(
      1,
      'audio',
      t(
        'addcase.bootstrap_audio_message',
        'Uploading your recording and preparing the first items for project setup...'
      )
    );

    setUploadedDatasourceFiles((current) => mergeDatasourceFiles(
      current,
      [{ name: pendingAudioReview.file.name, size: pendingAudioReview.file.size }]
    ));

    const audioFile = pendingAudioReview.file;
    setIsAudioReviewOpen(false);

    ensureTemporaryProject()
      .then((createdCaseId) => {
        uploadDatasourceFiles(createdCaseId, [audioFile]);
        clearPendingAudioReview();
      })
      .catch(() => {
        setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to upload the dropped files.'));
        setStatus('datasource');
      });
  }, [
    beginBootstrapForDatasource,
    clearPendingAudioReview,
    ensureTemporaryProject,
    pendingAudioReview,
    t,
    uploadDatasourceFiles,
  ]);

  const handleFinalizeProject = useCallback(() => {
    if (!temporaryCaseId || !goal.trim()) {
      return;
    }

    shouldAwaitCreationUpdatesRef.current = true;
    startCaseCreation();

    sendMessage({
      command: 'FinalizeProjectCreation',
      caseid: temporaryCaseId,
      goal: goal.trim(),
    });
    setStatus('updating');
  }, [goal, sendMessage, temporaryCaseId]);

  const handleManualCreateProject = useCallback((projectData = {}) => {
    if (!sendMessage || !isConnected) {
      setDatasourceUploadError(
        t('addcase.datasource_upload_error', 'Unable to create the project right now. Please try again.')
      );
      setStatus('datasource');
      return false;
    }

    manualProjectCreationRef.current = true;
    shouldAwaitCreationUpdatesRef.current = false;
    setDatasourceUploadError('');
    setUpdateMessage(t('addcase.creating_project', 'Creating your project...'));
    setStatus('updating');
    startCaseCreation();

    sendMessage({
      command: 'AddProject',
      folder: '',
      title: String(projectData?.title || '').trim(),
      description: String(projectData?.description || '').trim(),
      goal: '',
      customFields: Array.isArray(projectData?.customFields) ? projectData.customFields : [],
      attachments: [],
    });
    return true;
  }, [isConnected, sendMessage, t]);

  const applyResumeCaseState = useCallback((payload = {}) => {
    const caseData = payload?.case;
    if (!caseData) {
      return false;
    }

    const creationWizardState = payload?.creationWizardState || {};
    const resumedCaseId = caseData.id || resumeCaseId;
    const resumedStep = String(creationWizardState.step || '').toLowerCase();
    const resumedGoal = String(caseData.goal || '');
    const resumedTargetCount = resolveBootstrapTargetCount(
      creationWizardState.bootstrapFileCount,
      creationWizardState.bootstrapTargetCount
    );

    setTemporaryCaseId(resumedCaseId);
    setGoal(resumedGoal);
    setAutoOpenConnector('');
    setDatasourceUploadError('');
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    shouldAwaitCreationUpdatesRef.current = resumedStep === 'updating';

    if (resumedStep === 'datasource') {
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(false);
      setStatus('datasource');
      return true;
    }

    if (resumedStep === 'bootstrap') {
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(false);
      setBootstrapProgress((current) => ({
        ...current,
        phase: 'waiting',
        processedCount: 0,
        queuedCount: creationWizardState.bootstrapFileCount || current.queuedCount || 0,
        targetCount: resumedTargetCount,
        message: t(
          'addcase.bootstrap_waiting_message',
          'Waiting for the first datasource items to be ready...'
        ),
        latestItem: null,
      }));
      setStatus('bootstrap');
      return true;
    }

    if (resumedStep === 'question') {
      setPendingCaseId(null);
      setUpdateMessage('');
      setBootstrapReady(true);
      setStatus('question');
      return true;
    }

    if (resumedStep === 'updating') {
      setPendingCaseId(resumedCaseId);
      setBootstrapReady(true);
      setUpdateMessage(
        creationWizardState.message || t('addcase.creating_project', 'Creating your project...')
      );
      setStatus('updating');
      return true;
    }

    return false;
  }, [resumeCaseId, t]);

  const handleBootstrapFailure = useCallback((errorMessage) => {
    clearCaseCreation();
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    setBootstrapReady(false);
    const resolvedErrorMessage = errorMessage || t('addcase.datasource_upload_error', 'Unable to process this datasource.');
    setBootstrapError(resolvedErrorMessage);
    setDatasourceUploadError(resolvedErrorMessage);
    setBootstrapProgress((current) => ({
      ...current,
      message: resolvedErrorMessage,
    }));
    setStatus('bootstrap');
  }, [t]);

  const resetWizardState = useCallback((nextStatus = 'datasource') => {
    shouldAwaitCreationUpdatesRef.current = false;
    temporaryProjectCreationRef.current = false;
    manualProjectCreationRef.current = false;
    temporaryProjectPromiseResolveRef.current = null;
    temporaryProjectPromiseRejectRef.current = null;

    setStatus(nextStatus);
    setGoal('');
    setUpdateMessage('');
    setTemporaryCaseId(null);
    setPendingCaseId(null);
    setAutoOpenConnector('');
    setConnectorWizardRequest(null);
    setIsPreparingTemporaryProject(false);
    setIsUploadingDatasource(false);
    setUploadProgress(0);
    setUploadedDatasourceFiles([]);
    setDatasourceUploadError('');
    setBootstrapError('');
    setIsAudioReviewOpen(false);
    clearPendingAudioReview();
    setBootstrapReady(false);
    setBootstrapProgress({
      phase: 'waiting',
      processedCount: 0,
      queuedCount: 0,
      targetCount: BOOTSTRAP_TARGET_COUNT,
      message: '',
      latestItem: null,
      sourceType: '',
    });

    clearCaseCreation();
  }, [clearPendingAudioReview]);

  const messageHandler = useCallback((parsedMessage) => {
    switch (parsedMessage.command) {
      case 'getcase': {
        const incomingCaseId = parsedMessage.data?.case?.id;
        const matchesResumeCase = resumeCaseId && String(incomingCaseId) === String(resumeCaseId);
        const matchesTemporaryCreationCase = temporaryCaseId && String(incomingCaseId) === String(temporaryCaseId);
        if (!matchesResumeCase && !matchesTemporaryCreationCase) {
          break;
        }
        if (!parsedMessage.data?.case) {
          setDatasourceUploadError(t('addcase.datasource_upload_error', 'Unable to load the project creation flow.'));
          resetWizardState('datasource');
          navigate('/add', { replace: true });
          break;
        }
        const handled = applyResumeCaseState(parsedMessage.data);
        if (!handled) {
          navigate(`/case/${incomingCaseId}`, { replace: true });
        }
        break;
      }
      case 'forceRunDatasourceComplete':
        if (String(parsedMessage.data?.status || '').toLowerCase() === 'success') {
          if (
            status === 'bootstrap'
            && temporaryCaseId
            && typeof sendMessage === 'function'
          ) {
            // Some connectors create events directly during force-run without
            // sending bootstrap progress updates. Re-read the temporary case so
            // the creation wizard can advance as soon as the case state is ready.
            sendMessage({ command: 'getcase', caseid: temporaryCaseId });
          }
        } else if (String(parsedMessage.data?.status || '').toLowerCase() === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_connector_error', 'Connector ingestion failed.')
          );
        }
        break;
      case 'forceRunDatasource':
        if (String(parsedMessage.data?.status || '').toLowerCase() === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_connector_error', 'Connector ingestion failed.')
          );
        }
        break;
      case 'AddProject':
        if (parsedMessage.data?.status === 'success') {
          const createdCaseId = parsedMessage.data.id;
          if (manualProjectCreationRef.current) {
            manualProjectCreationRef.current = false;
            clearCaseCreation();
            window.location.href = `/case/${createdCaseId}`;
            return;
          }
          if (temporaryProjectCreationRef.current) {
            temporaryProjectCreationRef.current = false;
            setIsPreparingTemporaryProject(false);
            setTemporaryCaseId(createdCaseId);
            if (typeof temporaryProjectPromiseResolveRef.current === 'function') {
              temporaryProjectPromiseResolveRef.current(createdCaseId);
            }
            temporaryProjectPromiseResolveRef.current = null;
            temporaryProjectPromiseRejectRef.current = null;
            return;
          }

          // In the datasource-first wizard, AddProject is only used to create
          // the temporary project shell. Opening the dashboard must wait for
          // FinalizeProjectCreation -> CaseCreationReady.
          if (!temporaryCaseId && createdCaseId) {
            setTemporaryCaseId(createdCaseId);
            setStatus('datasource');
          }
        } else {
          if (manualProjectCreationRef.current) {
            manualProjectCreationRef.current = false;
            clearCaseCreation();
            setDatasourceUploadError(parsedMessage.data?.error || t('addcase.datasource_upload_error', 'Unable to create the project.'));
            setStatus('datasource');
            break;
          }
          if (temporaryProjectCreationRef.current) {
            temporaryProjectCreationRef.current = false;
            setIsPreparingTemporaryProject(false);
            setAutoOpenConnector('');
            setIsUploadingDatasource(false);
            setUploadProgress(0);
            if (typeof temporaryProjectPromiseRejectRef.current === 'function') {
              temporaryProjectPromiseRejectRef.current(new Error(parsedMessage.data?.error || 'Unable to create temporary project.'));
            }
            temporaryProjectPromiseResolveRef.current = null;
            temporaryProjectPromiseRejectRef.current = null;
            setStatus('datasource');
          }
        }
        break;
      case 'uploadfile': {
        handleDatasourceUploadResult(parsedMessage.data || {});
        break;
      }
      case 'CaseCreationBootstrapStatus': {
        const bootstrapCaseId = parsedMessage.data?.caseid;
        if (!bootstrapCaseId || String(bootstrapCaseId) !== String(temporaryCaseId)) {
          break;
        }
        if (shouldAwaitCreationUpdatesRef.current) {
          break;
        }

        const nextProcessedCount = Number.isFinite(Number(parsedMessage.data?.processedCount))
          ? Number(parsedMessage.data.processedCount)
          : 0;
        const nextQueuedCount = Number.isFinite(Number(parsedMessage.data?.queuedCount))
          ? Number(parsedMessage.data.queuedCount)
          : 0;
        const nextTargetCount = Number.isFinite(Number(parsedMessage.data?.targetCount))
          ? Number(parsedMessage.data.targetCount)
          : BOOTSTRAP_TARGET_COUNT;
        const incomingStatus = String(parsedMessage.data?.status || '').toLowerCase();
        if (incomingStatus === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_ingestion_error', 'Datasource ingestion failed.')
          );
          break;
        }
        const incomingReady = incomingStatus === 'ready';
        const shouldKeepReady = bootstrapReady || incomingReady;

        setBootstrapProgress((current) => ({
          ...current,
          phase: incomingReady ? 'ready' : current.phase,
          processedCount: shouldKeepReady
            ? Math.max(
              current.processedCount || 0,
              nextProcessedCount,
              resolveBootstrapTargetCount(nextQueuedCount, nextTargetCount)
            )
            : nextProcessedCount,
          queuedCount: nextQueuedCount,
          targetCount: resolveBootstrapTargetCount(nextQueuedCount, nextTargetCount),
          message: parsedMessage.data?.message || current.message,
          latestItem: parsedMessage.data?.latestItem || current.latestItem,
        }));

        if (incomingReady) {
          setBootstrapReady(true);
        }

        if (shouldKeepReady) {
          setStatus('question');
        } else {
          setStatus('bootstrap');
        }
        break;
      }
      case 'FinalizeProjectCreation':
        if (parsedMessage.data?.status === 'success') {
          setPendingCaseId(parsedMessage.data.id || temporaryCaseId);
          setStatus('updating');
        } else {
          clearCaseCreation();
          setStatus('question');
        }
        break;
      case 'CaseCreationReady': {
        const readyCaseId = parsedMessage.data?.caseid || pendingCaseId || temporaryCaseId;
        if (!readyCaseId) {
          break;
        }
        if (!pendingCaseId && !isCaseCreationActive()) {
          break;
        }
        setPendingCaseId(null);
        clearCaseCreation();
        if (parsedMessage.data?.reviewWidgets) {
          navigate(`/case/${readyCaseId}?reviewWidgets=true`);
        } else {
          navigate(`/case/${readyCaseId}`);
        }
        break;
      }
      case 'UpdateCreationCase': {
        if (!isCaseCreationActive()) {
          break;
        }
        setStatus('updating');
        setUpdateMessage(parsedMessage.data?.message || '');
        break;
      }
      case 'datasourceIngestionUpdate': {
        const matchesCase = parsedMessage.data?.caseid
          ? String(parsedMessage.data.caseid) === String(temporaryCaseId)
          : false;
        if (!matchesCase || status !== 'bootstrap') {
          break;
        }

        const ingestionStatus = String(parsedMessage.data?.status || '').toLowerCase();
        if (ingestionStatus === 'error') {
          handleBootstrapFailure(
            parsedMessage.data?.message || t('addcase.bootstrap_ingestion_error', 'Datasource ingestion failed.')
          );
        } else if (ingestionStatus === 'warning' || ingestionStatus === 'info') {
          setBootstrapProgress((current) => ({
            ...current,
            message: parsedMessage.data?.message || current.message,
          }));
        } else if (ingestionStatus === 'scanning') {
          setBootstrapProgress((current) => ({
            ...current,
            phase: 'scanning',
            message: t(
              'addcase.bootstrap_scanning_message',
              'Scanning the datasource to capture the first items for project setup...'
            ),
          }));
        } else if (ingestionStatus === 'idle' && !isUploadingDatasource) {
          setBootstrapProgress((current) => ({
            ...current,
            phase: 'waiting',
            message: current.message || t(
              'addcase.bootstrap_waiting_message',
              'Waiting for the first datasource items to be ready...'
            ),
          }));
        }
        break;
      }
      case 'certification':
      case 'configuration_data':
        if (parsedMessage.data?.server_version) {
          setServerVersion(parsedMessage.data.server_version);
        }
        break;
      default:
        break;
    }
  }, [applyResumeCaseState, bootstrapReady, handleBootstrapFailure, handleDatasourceUploadResult, isUploadingDatasource, navigate, pendingCaseId, resumeCaseId, sendMessage, status, t, temporaryCaseId]);

  useEffect(() => {
    if (lastResumeCaseIdRef.current === resumeCaseId) {
      return;
    }

    lastResumeCaseIdRef.current = resumeCaseId;
    resetWizardState(resumeCaseId ? 'updating' : 'datasource');
  }, [resetWizardState, resumeCaseId]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(messageHandler);
    return unsubscribe;
  }, [messageHandler, subscribeToMessages]);

  useEffect(() => {
    if (!resumeCaseId || !sendMessage) {
      return;
    }
    setStatus('updating');
    setUpdateMessage(t('addcase.creating_project', 'Creating your project...'));
    sendMessage({ command: 'getcase', caseid: resumeCaseId });
  }, [resumeCaseId, sendMessage, t]);

  useEffect(() => {
    if (!pendingCaseId || updateMessage) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setPendingCaseId(null);
      clearCaseCreation();
      navigate(`/case/${pendingCaseId}`);
    }, 240000);
    return () => clearTimeout(timeoutId);
  }, [navigate, pendingCaseId, updateMessage]);

  const canFinalizeProject = Boolean(temporaryCaseId) && Boolean(goal.trim());
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDatasourceDrop,
    noClick: false,
    multiple: true,
  });

  useEffect(() => {
    if (status !== 'bootstrap') {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      const timeoutMessage = t(
        'addcase.bootstrap_timeout_message',
        'The initial bootstrap is taking longer than expected. DataUnfold is still waiting for the first datasource items before unlocking the question.'
      );
      setBootstrapProgress((current) => ({
        ...current,
        message: timeoutMessage,
      }));
      setBootstrapError((current) => current || timeoutMessage);
    }, BOOTSTRAP_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [status, t]);

  const renderBrand = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 4,
        mt: { xs: 2, sm: 4 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          animation: 'fadeInUp 1s ease-out',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: '-20px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${primaryColorTransparent} 0%, transparent 70%)`,
            animation: 'pulse 3s infinite ease-in-out',
            opacity: 0.4,
          }}
        />
        <img
          src={logo}
          alt="DataUnfold Logo"
          style={{
            width: '100px',
            height: '100px',
            animation: 'glow 4s infinite ease-in-out, float 8s infinite ease-in-out',
            position: 'relative',
            zIndex: 2,
            filter: `drop-shadow(0 0 20px ${primaryColorTransparent})`,
          }}
        />
      </Box>
    </Box>
  );

  const creationSteps = [
    t('addcase.creation_step_datasource', 'Data'),
    t('addcase.creation_step_bootstrap', 'Bootstrap'),
    t('addcase.creation_step_question', 'Question'),
  ];

  const renderCreationStepper = (activeStep) => (
    <Box sx={{ px: { xs: 0, sm: 2 }, mb: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {creationSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  if (status === 'updating') {
    return <LoadingScreen status={status} updateMessage={updateMessage} />;
  }

  if (status === 'bootstrap') {
    const bootstrapProgressValue = bootstrapProgress.targetCount > 0
      ? Math.min(100, Math.round((bootstrapProgress.processedCount / bootstrapProgress.targetCount) * 100))
      : 0;
    const bootstrapPhaseLabel = t(
      `addcase.bootstrap_phase_${bootstrapProgress.phase || 'waiting'}`,
      bootstrapProgress.phase || 'waiting'
    );

    return (
      <Box
        className="container"
        sx={{
          backgroundColor: '#f7f7ff',
          background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
          backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: '960px', width: '100%', mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: '28px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
              boxShadow: '0 18px 40px rgba(87, 87, 176, 0.12)',
              border: '1px solid rgba(87, 87, 176, 0.08)',
            }}
          >
            {renderBrand()}
            <Typography
              variant="h4"
              sx={{ color: primaryColor, fontWeight: 700, textAlign: 'center', mb: 1.5 }}
            >
              {t('addcase.bootstrap_title', 'Preparing the first project context')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '760px', mx: 'auto', mb: 4, lineHeight: 1.7 }}
            >
              {t(
                'addcase.bootstrap_subtitle',
                'DataUnfold is uploading and ingesting the first 5 files or events so it can build the title, description, tags, and widgets on the right context.'
              )}
            </Typography>

            {renderCreationStepper(1)}

            <Box
              sx={{
                borderRadius: '22px',
                border: '1px solid rgba(87, 87, 176, 0.12)',
                background: 'rgba(255,255,255,0.82)',
                p: { xs: 3, sm: 4 },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Chip
                  label={t('addcase.bootstrap_phase_prefix', 'Phase: {{phase}}', { phase: bootstrapPhaseLabel })}
                  sx={{
                    backgroundColor: 'rgba(87, 87, 176, 0.08)',
                    border: '1px solid rgba(87, 87, 176, 0.14)',
                    color: primaryColor,
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
                {isUploadingDatasource || isPreparingTemporaryProject ? (
                  <CircularProgress size={28} sx={{ color: primaryColor }} />
                ) : (
                  <CircularProgress size={28} sx={{ color: primaryColor }} />
                )}
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                  {bootstrapProgress.message || t('addcase.bootstrap_waiting_message', 'Waiting for the first datasource items to be ready...')}
                </Typography>
              </Box>

              {bootstrapError && (
                <Alert
                  severity="error"
                  sx={{ maxWidth: '720px', mx: 'auto', mb: 3, alignItems: 'center' }}
                  action={(
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setBootstrapError('');
                        setStatus('datasource');
                      }}
                    >
                      {t('addcase.bootstrap_back_to_datasource', 'Back to datasource')}
                    </Button>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('addcase.bootstrap_error_title', 'Bootstrap failed')}
                  </Typography>
                  <Typography variant="body2">
                    {bootstrapError}
                  </Typography>
                </Alert>
              )}

              {(isUploadingDatasource || isPreparingTemporaryProject) && (
                <Box sx={{ maxWidth: '560px', mx: 'auto', mb: 3 }}>
                  <LinearProgress
                    variant={isUploadingDatasource ? 'determinate' : 'indeterminate'}
                    value={Math.max(5, Math.min(100, uploadProgress))}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: 'rgba(87, 87, 176, 0.12)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: primaryColor,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
                    {isPreparingTemporaryProject
                      ? t('addcase.datasource_preparing_project', 'Preparing the temporary project...')
                      : t('addcase.datasource_uploading', 'Uploading datasource files...')}
                  </Typography>
                </Box>
              )}

              <Box sx={{ maxWidth: '620px', mx: 'auto' }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(6, bootstrapProgressValue)}
                  sx={{
                    height: 12,
                    borderRadius: 999,
                    backgroundColor: 'rgba(87, 87, 176, 0.10)',
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1.25, color: 'text.secondary', textAlign: 'center' }}>
                  {t(
                    'addcase.bootstrap_counter',
                    '{{count}} / {{target}} bootstrap items ready',
                    { count: bootstrapProgress.processedCount, target: bootstrapProgress.targetCount }
                  )}
                </Typography>
              </Box>

              {bootstrapProgress.latestItem?.file_name && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                  <Chip
                    label={t(
                      'addcase.bootstrap_latest_item',
                      'Latest item: {{name}}',
                      { name: bootstrapProgress.latestItem.file_name }
                    )}
                    sx={{
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      border: '1px solid rgba(87, 87, 176, 0.14)',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="container"
      sx={{
        backgroundColor: '#f7f7ff',
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
        backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        position: 'relative',
      }}
    >
      <Box sx={{ maxWidth: '980px', width: '100%', mx: 'auto' }}>
        {status === 'datasource' ? (
          <Box sx={{ mt: 6, mb: 4 }}>
            {!resumeCaseId && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setTemplateSelectionOpen(true)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    color: primaryColor,
                    borderColor: 'rgba(87, 87, 176, 0.24)',
                    backgroundColor: 'rgba(255,255,255,0.82)',
                  }}
                >
                  {t('addcase.use_template', 'Use Template')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setManualCreationDialogOpen(true)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    color: primaryColor,
                    borderColor: 'rgba(87, 87, 176, 0.24)',
                    backgroundColor: 'rgba(255,255,255,0.82)',
                  }}
                >
                  {t('addcase.manual_creation_button', 'Manual Creation')}
                </Button>
              </Box>
            )}
            <Box sx={{ textAlign: 'center' }}>
              {renderBrand()}
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: primaryColor,
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '2rem',
                }}
              >
                {t('addcase.datasource_title', 'Create your project')}
              </Typography>
              {renderCreationStepper(0)}
              <Typography
                variant="body1"
                sx={{
                  maxWidth: '760px',
                  mx: 'auto',
                  mb: 4,
                  color: 'text.secondary',
                  lineHeight: 1.7,
                }}
              >
                {t(
                  'addcase.datasource_subtitle',
                  'Choose a datasource first. The project will be created with a temporary name and description as soon as you open a datasource to configure it.'
                )}
              </Typography>
            </Box>

            {datasourceUploadError && (
              <Box
                sx={{
                  mb: 3,
                  mx: 'auto',
                  maxWidth: '760px',
                  p: 2,
                  borderRadius: '18px',
                  border: '1px solid rgba(198, 40, 40, 0.2)',
                  backgroundColor: 'rgba(255, 235, 238, 0.85)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                }}
              >
                <WarningAmberIcon sx={{ color: '#c62828', fontSize: '1.3rem', mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#8e1c1c', fontWeight: 600 }}>
                  {datasourceUploadError}
                </Typography>
              </Box>
            )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: { xs: 2.5, lg: 3 },
                  alignItems: 'start',
                }}
              >
              <Paper
                {...getRootProps()}
                elevation={0}
                sx={{
                  ...datasourceCardSx,
                  border: isDragActive
                    ? `2px dashed ${primaryColor}`
                    : '2px dashed rgba(87, 87, 176, 0.22)',
                  background: isDragActive
                    ? 'linear-gradient(135deg, rgba(87, 87, 176, 0.12) 0%, rgba(255,255,255,0.98) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,247,255,0.96) 100%)',
                  boxShadow: isDragActive
                    ? '0 18px 42px rgba(87, 87, 176, 0.16)'
                    : '0 10px 30px rgba(87, 87, 176, 0.08)',
                  cursor: 'pointer',
                }}
              >
                <input {...getInputProps()} />
                <Box sx={datasourceCardHeaderSx}>
                  <CloudUploadIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                    {isDragActive
                      ? t('addcase.datasource_drop_active', 'Drop files to create the datasource')
                      : t('addcase.datasource_drop_title', 'Drag and drop files here')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.datasource_drop_subtitle', 'Upload one or more files to build the first datasource for this project.')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon fontSize="small" />}
                    sx={{
                      mt: 1,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {t('addcase.datasource_drop_browse', 'Choose files')}
                  </Button>

                  {uploadedDatasourceFiles.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 2 }}>
                      {uploadedDatasourceFiles.slice(0, 5).map((file) => (
                        <Chip
                          key={`${file.name}-${file.size ?? 'unknown'}`}
                          label={file.size ? `${file.name} (${formatBytes(file.size)})` : file.name}
                          sx={{
                            backgroundColor: 'rgba(87, 87, 176, 0.08)',
                            border: '1px solid rgba(87, 87, 176, 0.14)',
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {isUploadingDatasource && (
                    <Box sx={{ width: '100%', maxWidth: '520px', mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(5, Math.min(100, uploadProgress))}
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: primaryColor,
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t('addcase.datasource_uploading', 'Uploading datasource files...')}
                      </Typography>
                    </Box>
                  )}

                  {isPreparingTemporaryProject && !isUploadingDatasource && (
                    <Box sx={{ width: '100%', maxWidth: '520px', mt: 2 }}>
                      <LinearProgress
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: 'rgba(87, 87, 176, 0.12)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: primaryColor,
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {t('addcase.datasource_preparing_project', 'Preparing the temporary project...')}
                      </Typography>
                    </Box>
                  )}

                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={datasourceCardSx}
              >
                <Box sx={datasourceCardHeaderSx}>
                  <MicIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: primaryColor,
                      fontWeight: 700,
                    }}
                  >
                    {t('addcase.project_audio_title', 'Record audio')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.project_audio_subtitle', 'Record a voice note and use it as the first datasource for this project.')}
                  </Typography>

                  <Button
                    variant={isRecordingAudio ? 'contained' : 'outlined'}
                    color={isRecordingAudio ? 'error' : 'primary'}
                    startIcon={isRecordingAudio ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                    onClick={isRecordingAudio ? stopAudioRecording : startAudioRecording}
                    disabled={isUploadingDatasource || isPreparingTemporaryProject}
                    sx={{
                      mt: 1,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {isRecordingAudio
                      ? t('addcase.project_audio_stop', 'Stop recording')
                      : t('addcase.project_audio_start', 'Start recording')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<MicExternalOnIcon fontSize="small" />}
                    onClick={() => setIsMixedConsentOpen(true)}
                    disabled={isUploadingDatasource || isPreparingTemporaryProject || isRecordingAudio || !canUseDisplayAudio}
                    sx={{
                      mt: 1.25,
                      ...wizardLauncherButtonSx,
                    }}
                  >
                    {t('addcase.project_audio_start_system', 'Record system wide')}
                  </Button>

                  {pendingAudioReview?.name && !isAudioReviewOpen && (
                    <Chip
                      label={pendingAudioReview.name}
                      sx={{
                        mt: 2,
                        backgroundColor: 'rgba(87, 87, 176, 0.08)',
                        border: '1px solid rgba(87, 87, 176, 0.14)',
                        maxWidth: '100%',
                      }}
                    />
                  )}

                  {isRecordingAudio && (
                    <Typography variant="body2" sx={{ mt: 2, color: '#c62828', fontWeight: 600 }}>
                      {t('addcase.project_audio_recording_live', 'Recording... click stop when finished.')}
                    </Typography>
                  )}

                  {!canUseDisplayAudio && (
                    <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary', maxWidth: 320 }}>
                      {t('addcase.project_audio_system_hint', 'System-wide audio capture is available on desktop browsers that support screen sharing audio.')}
                    </Typography>
                  )}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={datasourceCardSx}
              >
                <Box sx={datasourceCardHeaderSx}>
                  <HubIcon sx={{ fontSize: 46, color: primaryColor }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: primaryColor,
                      fontWeight: 700,
                    }}
                  >
                    {t('addcase.project_connectors_title', 'Connecteur')}
                  </Typography>
                </Box>

                <Box sx={datasourceCardBodySx}>
                  <Typography variant="body2" sx={datasourceCardSubtitleSx}>
                    {t('addcase.project_connectors_subtitle', 'Connect email, cloud storage, or another service to start with live data.')}
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 320, mx: 'auto' }}>
                    <SettingDatasources
                    caseid={temporaryCaseId}
                    hideHeader
                    compactMode
                    showDescription={false}
                    compactLauncherBrowseLabel={t('addcase.project_connectors_browse', 'Parcourir')}
                    compactLauncherCreatorLabel={t('addcase.project_connectors_creator', 'Créateur')}
                    compactLauncherVertical
                    autoOpenConnector={autoOpenConnector}
                    onConnectorCreated={(details) => {
                      if (!details?.type) {
                        return;
                      }
                      setConnectorWizardRequest({
                        type: details.type,
                        title: details.title || details.type,
                      });
                    }}
                    onConnectorOpened={() => {
                      setAutoOpenConnector('');
                    }}
                    onDatasourceConfigured={handleDatasourceConfigured}
                    beforeOpenConnector={handleDatasourceBeforeOpen}
                    ensureCase={ensureTemporaryProject}
                    projectCreationWizardMode
                    />
                  </Box>
                  <ConnectorWizardHost
                    open={Boolean(connectorWizardRequest?.type)}
                    caseid={temporaryCaseId}
                    connectorType={connectorWizardRequest?.type || ''}
                    connectorTitle={connectorWizardRequest?.title || ''}
                    onClose={() => setConnectorWizardRequest(null)}
                    onDatasourceConfigured={(details) => {
                      setConnectorWizardRequest(null);
                      handleDatasourceConfigured(details);
                    }}
                    beforeOpen={handleDatasourceBeforeOpen}
                    ensureCase={ensureTemporaryProject}
                    projectCreationWizardMode
                  />
                </Box>
              </Paper>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 8, mb: 6 }}>
            {renderBrand()}
            {renderCreationStepper(2)}
            <Typography
              variant="h5"
              component="h2"
              sx={{
                color: primaryColor,
                fontWeight: 600,
                mb: 1.5,
                fontSize: '1.45rem',
              }}
            >
              {t('addcase.question_title')}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                maxWidth: '700px',
                mx: 'auto',
                mb: 2,
              }}
            >
              {t('addcase.question_subtitle')}
            </Typography>

            <Box sx={{ maxWidth: '700px', mx: 'auto', mb: 2 }}>
              <TextField
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={goal}
                onChange={(e) => setGoal(e.target.value.slice(0, 200))}
                placeholder={typedPlaceholder || t('addcase.input_placeholder')}
                inputProps={{ maxLength: 200 }}
                helperText={t('addcase.characters_left', { remaining: Math.max(0, 200 - goal.length) })}
                FormHelperTextProps={{
                  sx: {
                    textAlign: 'right',
                    maxWidth: '700px',
                    mx: 'auto',
                    color: 'rgba(87, 87, 176, 0.7)',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiInputBase-inputMultiline': {
                      fontSize: '1.2rem',
                      lineHeight: 1.6,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '1.1rem',
                      color: 'rgba(0, 0, 0, 0.38)',
                      opacity: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: primaryColor,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<CreateNewFolderIcon />}
              onClick={handleFinalizeProject}
              disabled={!canFinalizeProject}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: '50px',
                boxShadow: '0 4px 14px rgba(87, 87, 176, 0.25)',
                fontWeight: 600,
                background: !canFinalizeProject
                  ? 'linear-gradient(to right, #e0e0e0, #f5f5f5)'
                  : `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
                color: !canFinalizeProject ? '#b0b0b0' : '#fff',
                opacity: !canFinalizeProject ? 0.7 : 1,
                cursor: !canFinalizeProject ? 'not-allowed' : 'pointer',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                mt: 2,
                '&:hover': !canFinalizeProject
                  ? {
                      background: 'linear-gradient(to right, #e0e0e0, #f5f5f5)',
                      boxShadow: 'none',
                      transform: 'none',
                    }
                  : {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(87, 87, 176, 0.35)',
                      background: `linear-gradient(to right, ${primaryColor}, ${selectedColor})`,
                    },
              }}
            >
              {t('addcase.create_project')}
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
          {serverVersion ? `V: ${serverVersion}` : 'Server: unknown'}
        </Typography>
      </Box>

      <ManualCreationDialog
        open={manualCreationDialogOpen}
        onClose={() => setManualCreationDialogOpen(false)}
        onCreateProject={handleManualCreateProject}
      />

      <RecordingDialog
        open={isRecordingAudio}
        recordingElapsed={recordingElapsed}
        stopRecording={stopAudioRecording}
        waveformHistory={waveformHistory}
        maxRecordingDurationSeconds={maxRecordingDurationSeconds}
      />

      <AudioReviewDialog
        open={isAudioReviewOpen}
        onClose={() => setIsAudioReviewOpen(false)}
        onReRecord={() => {
          setIsAudioReviewOpen(false);
          clearPendingAudioReview();
          startAudioRecording();
        }}
        onUpload={handleUploadRecordedAudio}
        audioUrl={pendingAudioReview?.url}
        audioName={pendingAudioReview?.name}
        title={t('addcase.project_audio_review_title', 'Review recording')}
        hintText={t('addcase.project_audio_review_hint', 'Listen to your recording before uploading it to the new project.')}
        reRecordLabel={t('addcase.project_audio_rerecord', 'Record again')}
        cancelLabel={t('common.cancel', 'Cancel')}
        uploadLabel={t('addcase.project_audio_upload', 'Upload recording')}
      />

      <Dialog
        open={isMixedConsentOpen}
        onClose={() => setIsMixedConsentOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('addcase.mixed_recording_consent_title', 'Record microphone + system audio?')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: '1.75rem', mt: '2px' }} />
            <Typography variant="body2" sx={{ color: '#4b5563', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {t(
                'addcase.mixed_recording_consent_body',
                'Recording system audio will capture other participants. Please make sure everyone on the call consents before continuing.'
              )}
              <Typography component="span" variant="caption" sx={{ color: '#8b94a0' }}>
                {t('addcase.mixed_recording_share_audio_hint', 'To capture all audio, make sure the "Share Audio" toggle in the screen-sharing prompt is on.')}
              </Typography>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setIsMixedConsentOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsMixedConsentOpen(false);
              startAudioRecording('mixed');
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('addcase.mixed_recording_consent_confirm', 'Continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateSelectionDialog
        open={templateSelectionOpen}
        onClose={() => setTemplateSelectionOpen(false)}
        onSelectTemplate={() => {}}
      />
    </Box>
  );
};

export default AddProject;
